import { Grid, Switch, Typography } from "@mui/material";

import { Resolver, SubmitHandler, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { UGDiagramData } from "../types";
import TextInput from "../../../main/components/TextInput";
import { useEffect, useState } from "react";

const schema = yup.object({
  hasSmartLogger: yup.boolean().optional().nullable(),
  hasTransformer: yup.boolean().optional().nullable(),
  disjuntorTag: yup.string().optional().nullable(),
});

// Defina o tipo para o Resolver
type MyResolver = Resolver<yup.InferType<typeof schema>>;

interface EditUGDiagramDataProps {
  uG: {
    hasTransformer?: boolean;
    hasSmartLogger?: boolean;
    disjuntorTag?: string;
  };
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  updateStateContent: (
    field: string,
    value?: any
  ) => void;
}

const EditUGDiagramDataUsina: React.FC<EditUGDiagramDataProps> = ({
  uG,
  isEditing,
  updateStateContent,
}) => {
  const [hasTransformer, setHasTransformer] = useState(true);
  const [hasSmartLogger, setHasSmartLogger] = useState(true);

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      disjuntorTag: uG?.disjuntorTag,
      hasTransformer: uG?.hasTransformer,
      hasSmartLogger: uG?.hasSmartLogger
    },
    resolver: yupResolver(schema) as MyResolver,
  });

  type UGDiagramField = keyof UGDiagramData;

  const handleValueChange = (field: UGDiagramField, value: any) => {
    setValue(field, value);
  };

  const onSubmit: SubmitHandler<UGDiagramData> = async (data) => {
    handleValueChange("disjuntorTag", String(data.disjuntorTag));
    handleValueChange("hasSmartLogger", String(data.hasSmartLogger));
    handleValueChange("hasTransformer", String(data.hasTransformer));

    if (uG?.disjuntorTag !== data.disjuntorTag) {
      updateStateContent("disjuntorTag", data.disjuntorTag);
    }
    if (uG?.hasSmartLogger !== data.hasSmartLogger) {
      updateStateContent("hasSmartLogger", data.hasSmartLogger);
    }
    if (uG?.hasTransformer !== data.hasTransformer) {
      updateStateContent("hasTransformer", data.hasTransformer);
    }
  };

  const disjuntorTag = watch("disjuntorTag");
  const watchHasSmartLogger = watch("hasSmartLogger");
  const watchHasTransformer = watch("hasTransformer");

  useEffect(() => {
    updateStateContent("disjuntorTag", getValues("disjuntorTag"));
    updateStateContent("hasSmartLogger", getValues("hasSmartLogger"));
    updateStateContent("hasTransformer", getValues("hasTransformer"));
  }, [disjuntorTag, watchHasSmartLogger, watchHasTransformer]);

  useEffect(() => {
    setValue(`hasTransformer`, hasTransformer);
  }, [hasTransformer]);

  useEffect(() => {
    setValue(`hasSmartLogger`, hasSmartLogger);
  }, [hasSmartLogger]);

  useEffect(() => {
    setHasTransformer(uG.hasTransformer ?? false);
    setHasSmartLogger(uG.hasSmartLogger ?? false);
  }, []);

  return (
    <Grid
      container
      sx={{
        padding: "20px",
        flexDirection: "row",
        display: "flex",
        backgroundColor: "rgba(217, 217, 217, 0.1)",
        borderRadius: 2,
        marginTop: "8px",
        marginBottom: "8px",
        rowGap: "16px ",
        width: "100%",
      }}
    >
      <Grid item xs={6} textAlign={"start"}>
        <Typography>Dados do diagrama</Typography>
      </Grid>

      <Grid container spacing={2}>
        <Grid
          container
          spacing={2}
          sx={{
            padding: "20px",
          }}
          onSubmit={handleSubmit(onSubmit)}
        >
          <>
            <Grid sx={{ display: "flex", alignItems: "flex-end" }} item xs={12} md={3}>
              <TextInput
                control={control}
                fieldName="disjuntorTag"
                label={"Tag do disjuntor"}
                errors={errors}
                disabled={!isEditing}
                sx={{
                  width: "100%",
                  marginBottom: "10px",
                }}
              />
            </Grid>
            <Grid item xs={12} md={4} display={"flex"}>
              <Grid>
                <Typography variant="subtitle1" sx={{ marginTop: "15px" }}>
                  A UG possui transformador?
                </Typography>
                Não
                <Switch
                  checked={hasTransformer}
                  disabled={!isEditing}
                  onChange={(_, checked) => {
                    setHasTransformer(checked);
                  }}
                />
                Sim
              </Grid>
            </Grid>
            <Grid item xs={12} md={4} display={"flex"}>
              <Grid>
                <Typography variant="subtitle1" sx={{ marginTop: "15px" }}>
                  A UG possui smart logger?
                </Typography>
                Não
                <Switch
                  checked={hasSmartLogger}
                  disabled={!isEditing}
                  onChange={(_, checked) => {
                    setHasSmartLogger(checked);
                  }}
                />
                Sim
              </Grid>
            </Grid>
          </>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default EditUGDiagramDataUsina;
